import { Box, SxProps, useTheme } from '@mui/material';
import React, { Children, FC, PropsWithChildren } from 'react';
import { Swiper, SwiperProps, SwiperSlide } from 'swiper/react';

import { Navigation } from 'swiper';
import { SystemStyleObject } from '@mui/system';
import { getDirection } from '@/lang/utils';
import { useRouter } from 'next/router';

type MuviTabsProps = {
  sx?: Partial<SxProps>;
  scrollButtonSx?: SystemStyleObject;
  nextScrollButtonSx?: SystemStyleObject;
  prevScrollButtonSx?: SystemStyleObject;
  slideSx?: SystemStyleObject;
  wrapperSx?: SystemStyleObject;
  swiperSx?: SystemStyleObject;
  initialSlide?: number;
  slidesPerView?: number | 'auto';
  centeredSlides?: boolean;
  centeredSlidesBounds?: boolean;
  slideToClickedSlide?: boolean;
  slidesPerGroupAuto?: boolean;
};

const swiperProps: SwiperProps = {
  modules: [Navigation],
  navigation: true,
  pagination: false,
  loop: false,
  slidesPerView: 'auto',
  freeMode: false,
  spaceBetween: 13,
};

const emptySx: SxProps = {};
const emptySSO: SystemStyleObject = {};

const MuviTabs: FC<
  PropsWithChildren & MuviTabsProps & Pick<SwiperProps, 'centerInsufficientSlides'>
> = ({
  sx = emptySx,
  scrollButtonSx = emptySSO,
  nextScrollButtonSx = emptySSO,
  prevScrollButtonSx = emptySSO,
  wrapperSx = emptySSO,
  slideSx = emptySSO,
  swiperSx = emptySSO,
  children,
  initialSlide = 0,
  slidesPerView = 'auto',
  centeredSlides = false,
  centeredSlidesBounds = false,
  centerInsufficientSlides = false,
  slideToClickedSlide = false,
  slidesPerGroupAuto = false,
}) => {
  const theme = useTheme();

  const { locale } = useRouter();
  const dir = getDirection(locale);

  return (
    <Box
      sx={{
        '& .swiper-button-next, .swiper-button-prev': {
          backgroundColor: theme.palette.secondary.main,
          color: theme.palette.common.white,
          display: 'flex',
          justifyContent: 'center',
          width: { tablet: '24px', mobile: '20px' },
          height: { tablet: '34px', mobile: '29px' },
          zIndex: '99',
          padding: '4px',
          transition: 'opacity 0.2s ease-out',
          '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            filter: 'brightness(0.85)',
          },
          '&::after': {
            fontSize: { tablet: '20px', mobile: '13px' },
            fontWeight: 'bold',
          },
          top: { tablet: '60%', mobile: '70%' },
          userSelect: 'none',

          ...scrollButtonSx,
        },

        '& .swiper-button-next': {
          borderStartStartRadius: '20px',
          borderStartEndRadius: '0px',
          borderEndEndRadius: '0px',
          borderEndStartRadius: '20px',

          right: 0,
          left: 'auto',

          ...nextScrollButtonSx,
        },

        '& .swiper-button-prev': {
          borderStartStartRadius: '0px',
          borderStartEndRadius: '20px',
          borderEndEndRadius: '20px',
          borderEndStartRadius: '0px',

          left: 0,
          right: 'auto',

          ...prevScrollButtonSx,
        },

        '& .swiper-slide': {
          width: '4.375rem',
          ...slideSx,
        },

        '& .swiper-button-disabled': {
          opacity: 0,
          visibility: 'hidden',
        },

        '& .swiper-wrapper': {
          ...wrapperSx,
        },

        '& .swiper': {
          ...swiperSx,
        },
        ...sx,
      }}
    >
      <Swiper
        {...swiperProps}
        dir={dir}
        grabCursor
        initialSlide={initialSlide}
        slidesPerView={slidesPerView}
        centeredSlides={centeredSlides}
        centeredSlidesBounds={centeredSlidesBounds}
        centerInsufficientSlides={centerInsufficientSlides}
        slideToClickedSlide={slideToClickedSlide}
        slidesPerGroupAuto={slidesPerGroupAuto}
      >
        {Children.map(children, (child, i) => (
          <SwiperSlide key={i}>{child}</SwiperSlide>
        ))}
      </Swiper>
    </Box>
  );
};

export default MuviTabs;
